import { useCallback, useEffect, useRef, useState } from 'react';
import { play, stopPlayback } from '../lib/audio';
import { record, releaseMic } from '../lib/recorder';
import { ask as askAssistant, synthesize, synthesizeRate, transcribe, voiceStatus } from '../lib/voiceApi';

/**
 * The ear and the voice as one machine with a single `phase`:
 * idle → listening → thinking → idle (thinking also while the Assistant is asked),
 * and idle → speaking → idle.
 *
 * `status.ready` is null until the server has answered, then true or false.
 */
export function useVoice() {
  const [status, setStatus] = useState({ ready: null, voices: [], speakers: [] });
  const [phase, setPhase] = useState('idle');
  // `turn` is bumped by every new action, so a stale one cannot touch the phase;
  // `recording` is the AbortController of the utterance being recorded.
  const session = useRef({ turn: 0, recording: null }).current;

  useEffect(() => {
    let mounted = true;
    voiceStatus().then((s) => { if (mounted) setStatus(s); });
    return () => { mounted = false; session.turn++; stopPlayback(); releaseMic(); };
  }, [session]);

  /** Record one utterance spoken in `lang` and transcribe it: { transcription } | { noSpeech } | { cancelled }. */
  const listen = useCallback(async (lang, onLevel) => {
    const mine = ++session.turn;
    stopPlayback();
    session.recording = new AbortController();
    setPhase('listening');
    try {
      const clip = await record({ onLevel, signal: session.recording.signal });
      if (session.turn !== mine) return { cancelled: true };
      if (!clip) return { noSpeech: true };
      setPhase('thinking');
      const heard = await transcribe(clip, lang);
      return session.turn === mine ? heard : { cancelled: true };
    } finally {
      releaseMic();
      if (session.turn === mine) setPhase('idle');
    }
  }, [session]);

  /** End the recording now and keep what was said. */
  const finishListening = useCallback(() => session.recording?.abort(), [session]);

  // Play what `clip()` resolves to. 'speaking' from the start, any fetch
  // included: the answer is already on screen, and a "one moment…" in between
  // would hide it again. Resolves true when it played to the end.
  const sound = useCallback(async (clip) => {
    const mine = ++session.turn;
    session.recording?.abort();
    setPhase('speaking');
    try {
      const bytes = await clip();
      if (session.turn !== mine) return false;
      return await play(bytes);
    } finally {
      if (session.turn === mine) setPhase('idle');
    }
  }, [session]);

  /** Say `answer` ({ source, targets }) with `voice` ({ lang, voice }). */
  const speak = useCallback((answer, voice) => sound(() => synthesize(answer, voice)), [sound]);

  /** Say the BRH's reference rate, in Kreyòl, with `voice` (a Kreyòl voice id). */
  const speakRate = useCallback((rate, voice) => sound(() => synthesizeRate(rate, voice)), [sound]);

  /** Play a clip already in hand — the Assistant's spoken reply. */
  const playClip = useCallback((bytes) => sound(() => bytes), [sound]);

  /** Put a question to the Assistant: { text, audio } | { cancelled }. */
  const ask = useCallback(async (request) => {
    const mine = ++session.turn;
    setPhase('thinking');
    try {
      const reply = await askAssistant(request);
      return session.turn === mine ? reply : { cancelled: true };
    } finally {
      if (session.turn === mine) setPhase('idle');
    }
  }, [session]);

  /** Stop whatever is happening. */
  const hush = useCallback(() => {
    session.turn++;
    session.recording?.abort();
    stopPlayback();
    setPhase('idle');
  }, [session]);

  return { status, phase, listen, finishListening, speak, speakRate, playClip, ask, hush };
}
